import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../app/Auth';
import { ErrorNote, SecretField } from '../app/ui';
import { assertPasskey, enrollPasskey, mfaStatus, passkeysSupported } from '../lib/passkey';
import { getToken } from '../lib/api';
import { isHandoffSession } from '../lib/handoff';
import { HandoffStepUp } from './HandoffStepUp';

/**
 * Shown after a successful password sign-in while Nexus still requires a passkey for Helm (403 PASSKEY_REQUIRED).
 * Enrolled -> one tap (on a computer without biometrics the browser offers "use a phone or tablet": scan the QR code,
 * approve with Face ID). Not enrolled -> add the first passkey, confirming the password again; the recovery codes
 * shown once afterwards are the fallback.
 *
 * A Helm XR web window (hand-off session, amr web_handoff) cannot use a passkey (GeckoView has no FIDO2), so it shows
 * the hand-off step-up instead (O1/O2/O3, Erwin 2026-10-29); the passkey button stays as a secondary option for when
 * the same session is somehow in a real browser.
 */
export function PasskeyStep() {
  const { passkeyDone, signOut } = useAuth();
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [handoff] = useState(() => isHandoffSession(getToken()));
  const [usePasskeyHere, setUsePasskeyHere] = useState(false);

  useEffect(() => { mfaStatus().then((s) => setEnrolled(s.enrolled)).catch((e) => setError(e instanceof Error ? e.message : String(e))); }, []);

  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); setPassword(''); }
  }
  const verify = () => run(async () => { await assertPasskey(); await passkeyDone(); });
  const enroll = (e: FormEvent) => { e.preventDefault(); void run(async () => {
    const r = await enrollPasskey('Helm web passkey', password);
    if (r.recoveryCodes?.length) setCodes(r.recoveryCodes); else await passkeyDone();
  }); };

  if (codes) {
    return (
      <div className="center-screen"><div className="card signin">
        <h1>Save your recovery codes</h1>
        <p className="muted">Each works once if you lose your passkeys. They are shown only now.</p>
        <pre className="mono">{codes.join('\n')}</pre>
        <button className="btn primary" onClick={() => void run(passkeyDone)}>I saved them — continue</button>
      </div></div>
    );
  }
  if (handoff && !usePasskeyHere) {
    return (
      <div className="center-screen">
        <div className="card signin">
          <h1>Confirm it’s you</h1>
          <HandoffStepUp onDone={passkeyDone} />
          <button className="btn" type="button" onClick={() => setUsePasskeyHere(true)}>Use a passkey in this window instead</button>
          <button className="btn" type="button" onClick={signOut}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="center-screen">
      <form className="card signin" onSubmit={enroll}>
        <h1>Confirm with your passkey</h1>
        {!passkeysSupported() ? <ErrorNote text="This browser cannot use passkeys. Try Safari, Chrome or Edge." /> : null}
        {enrolled === false ? (
          <>
            <p className="muted">Helm needs a passkey. Add one now: confirm your password, then approve on this device or scan the QR code with your phone.</p>
            <label htmlFor="pk-pw">Password</label>
            <SecretField id="pk-pw" value={password} onChange={setPassword} autoComplete="current-password" />
            <button className="btn primary" type="submit" disabled={busy || !password || !passkeysSupported()}>{busy ? 'Waiting…' : 'Add a passkey'}</button>
          </>
        ) : (
          <>
            <p className="muted">Use your passkey to finish signing in. No biometrics on this computer? Choose “use a phone or tablet” and scan the QR code.</p>
            <button className="btn primary" type="button" onClick={verify} disabled={busy || enrolled === null || !passkeysSupported()}>{busy ? 'Waiting…' : 'Use passkey'}</button>
          </>
        )}
        <ErrorNote text={error} />
        <button className="btn" type="button" onClick={signOut}>Cancel</button>
      </form>
    </div>
  );
}
